import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import SimpleNextJSChart from '@/components/charts/SimpleNextJSChart'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { apiService } from '@/services/api'
import { AIInsightsSection } from '@/pages/ai/AIInsightsSection'
import {
  posApiService,
  type ItemSalesRow,
  type CashierSalesRow,
  type IncomeReportData,
  type ReorderRow,
  type ExpiryRow,
  type DaySummaryData
} from '@/services/posApiService'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface ReportSummary {
  sales: number
  orders: number
  customers: number
  products: number
}

interface TrajectoryPoint {
  period: string
  value: number
  label: string
}

interface TopProduct {
  productId: string
  name: string
  quantity: number
}

interface CategorySale {
  category: string
  sales: number
  percentage: number
}

interface RecentTransaction {
  id: string
  reader: string
  total: number
  status: string
}

interface ReportsApiResponse {
  success: boolean
  data: {
    period: string
    from?: string
    to?: string
    summary: ReportSummary
    trajectory: TrajectoryPoint[]
    topProducts: TopProduct[]
    salesByCategory: CategorySale[]
    recentTransactions: RecentTransaction[]
  }
}

const defaultSummary: ReportSummary = {
  sales: 0,
  orders: 0,
  customers: 0,
  products: 0
}

type DatePreset = '1d' | '7d' | '30d' | 'week' | 'month' | 'year' | 'custom'
type ReportTab = 'overview' | 'item-sales' | 'cashier' | 'income' | 'bill-payments' | 'reorder' | 'expiry' | 'ai-insights'

function getDateRangeForPreset(preset: DatePreset): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  to.setHours(23, 59, 59, 999)
  from.setHours(0, 0, 0, 0)
  if (preset === '1d') from.setDate(from.getDate() - 1)
  else if (preset === '7d') from.setDate(from.getDate() - 7)
  else if (preset === '30d') from.setDate(from.getDate() - 30)
  else if (preset === 'week') {
    from.setDate(from.getDate() - from.getDay())
    to.setDate(from.getDate() + 6)
  } else if (preset === 'month') {
    from.setDate(1)
    to.setMonth(to.getMonth() + 1)
    to.setDate(0)
  } else if (preset === 'year') {
    from.setMonth(0, 1)
    to.setMonth(11, 31)
  }
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10)
  }
}

const TABS: { id: ReportTab; name: string; icon: string }[] = [
  { id: 'overview', name: 'Overview', icon: '📊' },
  { id: 'item-sales', name: 'Item Sales', icon: '📦' },
  { id: 'cashier', name: 'Cashier Sales', icon: '👤' },
  { id: 'income', name: 'Income', icon: '💰' },
  { id: 'bill-payments', name: 'Bill Payments', icon: '🧾' },
  { id: 'reorder', name: 'Reorder', icon: '🔄' },
  { id: 'expiry', name: 'Expiry', icon: '📅' },
  { id: 'ai-insights', name: 'AI Insights', icon: '🧠' }
]

interface BillPaymentReportData {
  totalPayments: number
  totalAmount: number
  totalCommission: number
  byType: Array<{ billType: string; count: number; amount: number; commission: number }>
  byDay: Array<{ date: string; count: number; amount: number; commission: number }>
}

const EXPIRY_FILTERS: { id: 'expired' | 'today' | 'month' | '14days'; label: string }[] = [
  { id: 'expired', label: 'Expired' },
  { id: 'today', label: 'Expiring Today' },
  { id: 'month', label: 'This Month' },
  { id: '14days', label: 'Next 14 Days' }
]

export const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [datePreset, setDatePreset] = useState<DatePreset>('7d')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [selectedTab, setSelectedTab] = useState<ReportTab>(() => {
    const valid = ['overview', 'item-sales', 'cashier', 'income', 'bill-payments', 'reorder', 'expiry', 'ai-insights'] as const
    return valid.includes(tabParam as ReportTab) ? (tabParam as ReportTab) : 'overview'
  })
  const [expiryFilter, setExpiryFilter] = useState<'expired' | 'today' | 'month' | '14days'>('expired')
  const [loading, setLoading] = useState(true)
  const [reportData, setReportData] = useState<ReportsApiResponse['data'] | null>(null)
  const [itemSalesData, setItemSalesData] = useState<ItemSalesRow[]>([])
  const [cashierData, setCashierData] = useState<CashierSalesRow[]>([])
  const [incomeData, setIncomeData] = useState<IncomeReportData | null>(null)
  const [reorderData, setReorderData] = useState<ReorderRow[]>([])
  const [expiryData, setExpiryData] = useState<ExpiryRow[]>([])
  const [daySummary, setDaySummary] = useState<DaySummaryData | null>(null)
  const [billPaymentReport, setBillPaymentReport] = useState<BillPaymentReportData | null>(null)
  const [showDayReport, setShowDayReport] = useState(false)
  const [dayReportDate, setDayReportDate] = useState(new Date().toISOString().slice(0, 10))
  const { showSuccess, showError } = useNotificationActions()

  const { from, to } = datePreset === 'custom'
    ? { from: customFrom, to: customTo }
    : getDateRangeForPreset(datePreset)

  const fetchOverview = useCallback(async () => {
    setLoading(true)
    try {
      const useRange = (datePreset === 'custom' && from && to) || datePreset === 'week' || datePreset === 'month' || datePreset === 'year'
      const periodParam = (datePreset === '1d' || datePreset === '7d' || datePreset === '30d') ? datePreset : '7d'
      const url = useRange && from && to
        ? `/stats/reports?from=${from}&to=${to}`
        : `/stats/reports?period=${periodParam}`
      const res = await apiService.get<ReportsApiResponse>(url)
      if (res.success && res.data) setReportData(res.data)
      else setReportData(null)
    } catch (error: any) {
      setReportData(null)
      showError('Failed to load reports', error?.response?.data?.message || 'Unable to load.')
    } finally {
      setLoading(false)
    }
  }, [datePreset, from, to, showError])

  // Sync selected tab from URL (e.g. /reports?tab=ai-insights)
  useEffect(() => {
    const t = searchParams.get('tab')
    const valid: ReportTab[] = ['overview', 'item-sales', 'cashier', 'income', 'bill-payments', 'reorder', 'expiry', 'ai-insights']
    if (t && valid.includes(t as ReportTab)) setSelectedTab(t as ReportTab)
  }, [searchParams])

  useEffect(() => {
    if (selectedTab === 'overview') fetchOverview()
  }, [selectedTab, fetchOverview])

  useEffect(() => {
    if (selectedTab !== 'overview' && selectedTab !== 'ai-insights') setLoading(true)
    let cancelled = false
    if (selectedTab === 'item-sales') {
      posApiService.getItemSalesReport({ from, to }).then(res => {
        if (!cancelled && res.success && res.data) setItemSalesData(res.data)
      }).finally(() => { if (!cancelled) setLoading(false) })
    } else if (selectedTab === 'cashier') {
      posApiService.getCashierSalesReport({ from, to }).then(res => {
        if (!cancelled && res.success && res.data) setCashierData(res.data)
      }).finally(() => { if (!cancelled) setLoading(false) })
    } else if (selectedTab === 'income') {
      posApiService.getIncomeReport({ from, to, groupBy: 'day' }).then(res => {
        if (!cancelled && res.success && res.data) setIncomeData(res.data)
      }).finally(() => { if (!cancelled) setLoading(false) })
    } else if (selectedTab === 'bill-payments') {
      apiService.get<{ success: boolean; data: BillPaymentReportData }>(`/stats/reports/bill-payments?from=${from}&to=${to}`).then(res => {
        if (!cancelled && res.success && res.data) setBillPaymentReport(res.data)
        else if (!cancelled) setBillPaymentReport(null)
      }).catch(() => { if (!cancelled) setBillPaymentReport(null) }).finally(() => { if (!cancelled) setLoading(false) })
    } else if (selectedTab === 'reorder') {
      posApiService.getReorderReport().then(res => {
        if (!cancelled && res.success && res.data) setReorderData(res.data)
      }).finally(() => { if (!cancelled) setLoading(false) })
    } else if (selectedTab === 'expiry') {
      posApiService.getExpiryReport(expiryFilter).then(res => {
        if (!cancelled && res.success && res.data) setExpiryData(res.data)
      }).finally(() => { if (!cancelled) setLoading(false) })
    }
    return () => { cancelled = true }
  }, [selectedTab, from, to, expiryFilter])

  const loadDaySummary = useCallback(async () => {
    try {
      const res = await posApiService.getDaySummary(dayReportDate)
      if (res.success && res.data) setDaySummary(res.data)
      else setDaySummary(null)
    } catch {
      setDaySummary(null)
    }
  }, [dayReportDate])

  useEffect(() => {
    if (showDayReport) loadDaySummary()
  }, [showDayReport, loadDaySummary])

  const summary = reportData?.summary ?? defaultSummary
  const trajectory = reportData?.trajectory ?? []
  const topProducts = reportData?.topProducts ?? []
  const salesByCategory = reportData?.salesByCategory ?? []
  const recentTransactions = reportData?.recentTransactions ?? []
  const chartData = trajectory.map(d => ({ x: d.period, y: d.value, label: d.label }))

  const handleExportReport = () => {
    try {
      const csvContent = `Report Type,Period,Sales,Orders,Customers,Products\n${selectedTab},${datePreset},${summary.sales},${summary.orders},${summary.customers},${summary.products}`
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ESALES-${selectedTab}-${from}-${to}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      showSuccess('Export Complete', 'Report exported to CSV.')
    } catch (error) {
      showError('Export Error', 'Unable to generate report file.')
    }
  }

  const formatCurrency = (n: number) => `Rs.${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Sachin Bookshop Analytics</h1>
            <p className="text-muted-foreground mt-2">Reports with custom date range and advanced breakdowns.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              className="bg-background/50 border border-border px-3 py-2 rounded-xl text-sm font-medium"
            >
              <option value="1d">Last 24h</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom range</option>
            </select>
            {datePreset === 'custom' && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="bg-background/50 border border-border px-3 py-2 rounded-xl text-sm"
                />
                <span className="text-muted-foreground">to</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="bg-background/50 border border-border px-3 py-2 rounded-xl text-sm"
                />
              </>
            )}
            <button onClick={() => setShowDayReport(true)} className="bg-muted/50 border border-border px-4 py-2 rounded-xl text-sm font-medium hover:bg-muted">
              📋 Day Report
            </button>
            <button onClick={handleExportReport} className="glow-button py-2 text-xs">
              📥 EXPORT
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setSelectedTab(tab.id)
                setSearchParams({ tab: tab.id })
              }}
              className={`flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedTab === tab.id ? 'bg-primary text-white' : 'bg-muted/20 text-muted-foreground hover:bg-muted/40'}`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {selectedTab === 'ai-insights' ? (
          <AIInsightsSection />
        ) : loading ? (
          <LoadingSpinner size="md" message="Loading report..." submessage={selectedTab} />
        ) : selectedTab === 'overview' ? (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { label: 'Total Revenue', value: formatCurrency(summary.sales), color: 'text-primary' },
                { label: 'Orders', value: summary.orders, color: 'text-green-400' },
                { label: 'Customers', value: summary.customers, color: 'text-blue-400' },
                { label: 'Items Sold', value: summary.products, color: 'text-yellow-400' }
              ].map((stat, i) => (
                <div key={i} className="glass-card">
                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">{stat.label}</p>
                  <p className={`text-3xl font-black ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="glass-card">
                <h3 className="text-xl font-bold mb-6 esales-text">Financial Trajectory</h3>
                <div className="h-84">
                  {chartData.length === 0 ? (
                    <p className="text-muted-foreground text-center py-10">No revenue data for this period.</p>
                  ) : (
                    <SimpleNextJSChart data={chartData} type="line" color="var(--primary)" />
                  )}
                </div>
              </div>
              <div className="glass-card">
                <h3 className="text-xl font-bold mb-6 esales-text">Top Products</h3>
                <div className="space-y-4">
                  {topProducts.length === 0 ? (
                    <p className="text-muted-foreground text-center py-10">No sales in this period.</p>
                  ) : (
                    topProducts.map((p, i) => (
                      <div key={p.productId || i} className="flex items-center justify-between p-3 bg-muted/10 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <span className="w-8 h-8 rounded-lg bg-primary/20 text-primary flex items-center justify-center font-black text-xs">{i + 1}</span>
                          <p className="font-bold text-sm">{p.name}</p>
                        </div>
                        <span className="font-black text-primary">{p.quantity} units</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="glass-card">
                <h3 className="text-xl font-bold mb-6 esales-text">Sales by Category</h3>
                {salesByCategory.length === 0 ? (
                  <p className="text-muted-foreground text-center py-6">No category sales.</p>
                ) : (
                  salesByCategory.map((c, i) => (
                    <div key={i} className="mb-4">
                      <div className="flex justify-between text-xs font-bold mb-2">
                        <span>{c.category}</span>
                        <span className="text-primary">{formatCurrency(c.sales)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${c.percentage}%` }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="glass-card">
                <h3 className="text-xl font-bold mb-6 esales-text">Recent Transactions</h3>
                {recentTransactions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-6">No recent transactions.</p>
                ) : (
                  recentTransactions.map((t, i) => (
                    <div key={t.id || i} className="flex justify-between items-center p-3 border-b border-border/50 last:border-0">
                      <div>
                        <span className="text-xs font-mono text-muted-foreground">{t.id}</span>
                        <span className="ml-2 font-bold text-sm">{t.reader}</span>
                      </div>
                      <span className="font-black text-primary">{formatCurrency(Number(t.total))}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : selectedTab === 'item-sales' ? (
          <div className="glass-card overflow-hidden">
            <h3 className="text-xl font-bold mb-4 esales-text">Item-wise Sales ({from} to {to})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Product</th>
                    <th className="text-left py-2 px-3">SKU</th>
                    <th className="text-right py-2 px-3">Qty Sold</th>
                    <th className="text-right py-2 px-3">Revenue</th>
                    <th className="text-right py-2 px-3">Avg Price</th>
                  </tr>
                </thead>
                <tbody>
                  {itemSalesData.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No item sales in this period.</td></tr>
                  ) : (
                    itemSalesData.map((r, i) => (
                      <tr key={r.productId || i} className="border-b border-border/50">
                        <td className="py-2 px-3 font-medium">{r.productName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{r.sku || '-'}</td>
                        <td className="text-right py-2 px-3">{r.quantity}</td>
                        <td className="text-right py-2 px-3 font-medium">{formatCurrency(r.revenue)}</td>
                        <td className="text-right py-2 px-3">{formatCurrency(r.avgPrice)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : selectedTab === 'cashier' ? (
          <div className="glass-card overflow-hidden">
            <h3 className="text-xl font-bold mb-4 esales-text">Cashier-wise Sales ({from} to {to})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Cashier</th>
                    <th className="text-right py-2 px-3">Invoices</th>
                    <th className="text-right py-2 px-3">Revenue</th>
                    <th className="text-right py-2 px-3">Avg Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {cashierData.length === 0 ? (
                    <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No cashier data in this period.</td></tr>
                  ) : (
                    cashierData.map((r, i) => (
                      <tr key={r.cashierId || i} className="border-b border-border/50">
                        <td className="py-2 px-3 font-medium">{r.cashierName}</td>
                        <td className="text-right py-2 px-3">{r.totalInvoices}</td>
                        <td className="text-right py-2 px-3 font-medium">{formatCurrency(r.totalRevenue)}</td>
                        <td className="text-right py-2 px-3">{formatCurrency(r.avgTransaction)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : selectedTab === 'income' ? (
          <div className="space-y-6">
            {incomeData && (
              <>
                <div className="glass-card">
                  <h3 className="text-xl font-bold mb-4 esales-text">Total Income: {formatCurrency(incomeData.totalIncome)}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="glass-card">
                    <h3 className="text-lg font-bold mb-4">By Payment Method</h3>
                    <ul className="space-y-2">
                      {incomeData.byPaymentMethod.map((m, i) => (
                        <li key={i} className="flex justify-between">
                          <span className="capitalize">{m.method}</span>
                          <span className="font-medium">{formatCurrency(m.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="glass-card">
                    <h3 className="text-lg font-bold mb-4">By Period</h3>
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {incomeData.byPeriod.map((p, i) => (
                        <li key={i} className="flex justify-between">
                          <span>{p.period}</span>
                          <span className="font-medium">{formatCurrency(p.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            )}
            {!incomeData && !loading && <p className="text-muted-foreground text-center py-10">No income data.</p>}
          </div>
        ) : selectedTab === 'bill-payments' ? (
          <div className="space-y-6">
            {billPaymentReport ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="glass-card">
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Total Payments</p>
                    <p className="text-3xl font-black text-blue-400">{billPaymentReport.totalPayments}</p>
                    <p className="text-sm text-muted-foreground mt-1">{formatCurrency(billPaymentReport.totalAmount)}</p>
                  </div>
                  <div className="glass-card">
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Commission Earned</p>
                    <p className="text-3xl font-black text-green-400">{formatCurrency(billPaymentReport.totalCommission)}</p>
                  </div>
                  <div className="glass-card">
                    <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Period</p>
                    <p className="text-lg font-bold">{from} to {to}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="glass-card overflow-hidden">
                    <h3 className="text-xl font-bold mb-4 esales-text">By Bill Type</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3">Bill Type</th>
                            <th className="text-right py-2 px-3">Count</th>
                            <th className="text-right py-2 px-3">Amount</th>
                            <th className="text-right py-2 px-3">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billPaymentReport.byType.length === 0 ? (
                            <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No bill payments in this period.</td></tr>
                          ) : (
                            billPaymentReport.byType.map((r, i) => (
                              <tr key={i} className="border-b border-border/50">
                                <td className="py-2 px-3 font-medium capitalize">{r.billType}</td>
                                <td className="text-right py-2 px-3">{r.count}</td>
                                <td className="text-right py-2 px-3">{formatCurrency(r.amount)}</td>
                                <td className="text-right py-2 px-3 text-green-400">{formatCurrency(r.commission)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="glass-card overflow-hidden">
                    <h3 className="text-xl font-bold mb-4 esales-text">Daily Trend</h3>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3">Date</th>
                            <th className="text-right py-2 px-3">Count</th>
                            <th className="text-right py-2 px-3">Amount</th>
                            <th className="text-right py-2 px-3">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billPaymentReport.byDay.map((r, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 px-3">{r.date}</td>
                              <td className="text-right py-2 px-3">{r.count}</td>
                              <td className="text-right py-2 px-3">{formatCurrency(r.amount)}</td>
                              <td className="text-right py-2 px-3 text-green-400">{formatCurrency(r.commission)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-10">No bill payment data for this period.</p>
            )}
          </div>
        ) : selectedTab === 'reorder' ? (
          <div className="glass-card overflow-hidden">
            <h3 className="text-xl font-bold mb-4 esales-text">Products Below Reorder Level</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Product</th>
                    <th className="text-left py-2 px-3">SKU</th>
                    <th className="text-right py-2 px-3">Current Stock</th>
                    <th className="text-right py-2 px-3">Reorder Level</th>
                    <th className="text-right py-2 px-3">Suggested Qty</th>
                    <th className="text-left py-2 px-3">Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {reorderData.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No products below reorder level.</td></tr>
                  ) : (
                    reorderData.map((r, i) => (
                      <tr key={r.productId || i} className="border-b border-border/50">
                        <td className="py-2 px-3 font-medium">{r.productName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{r.sku || '-'}</td>
                        <td className="text-right py-2 px-3">{r.currentStock}</td>
                        <td className="text-right py-2 px-3">{r.reorderLevel}</td>
                        <td className="text-right py-2 px-3">{r.suggestedReorderQty}</td>
                        <td className="py-2 px-3 text-muted-foreground">{r.supplierName || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : selectedTab === 'expiry' ? (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {EXPIRY_FILTERS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setExpiryFilter(f.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${expiryFilter === f.id ? 'bg-primary text-white' : 'bg-muted/20 hover:bg-muted/40'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="glass-card overflow-hidden">
              <h3 className="text-xl font-bold mb-4 esales-text">Expiry Report ({expiryFilter})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3">Product</th>
                      <th className="text-left py-2 px-3">Expiry Date</th>
                      <th className="text-right py-2 px-3">Days</th>
                      <th className="text-right py-2 px-3">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiryData.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No items match this filter.</td></tr>
                    ) : (
                      expiryData.map((r, i) => (
                        <tr key={r.id || i} className="border-b border-border/50">
                          <td className="py-2 px-3 font-medium">{r.name || r.title || 'Item'}</td>
                          <td className="py-2 px-3">{r.expiryDate?.slice(0, 10)}</td>
                          <td className="text-right py-2 px-3">{r.daysUntilExpiry}</td>
                          <td className="text-right py-2 px-3">{r.stock ?? '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {showDayReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDayReport(false)}>
            <div className="bg-background border border-border rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Day Summary</h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="date"
                  value={dayReportDate}
                  onChange={(e) => setDayReportDate(e.target.value)}
                  className="flex-1 bg-muted/30 border border-border rounded-lg px-3 py-2"
                />
                <button onClick={loadDaySummary} className="bg-primary text-white px-4 py-2 rounded-lg font-medium">Load</button>
              </div>
              {daySummary ? (
                <div className="space-y-3 text-sm">
                  <p><strong>Date:</strong> {daySummary.date}</p>
                  <p><strong>Total Sales:</strong> {formatCurrency(daySummary.totalSales)}</p>
                  <p><strong>Transactions:</strong> {daySummary.totalTransactions}</p>
                  <p><strong>Refunds:</strong> {formatCurrency(daySummary.totalRefunds)}</p>
                  <p><strong>Cash in hand:</strong> {formatCurrency(daySummary.cashInHand)}</p>
                  <p><strong>Card payments:</strong> {formatCurrency(daySummary.cardPayments)}</p>
                  <div>
                    <strong>Cashiers:</strong>
                    <ul className="mt-1 space-y-1">
                      {daySummary.cashierBreakdown.map((c, i) => (
                        <li key={i}>{c.cashierName}: {c.totalInvoices} invoices, {formatCurrency(c.totalRevenue)}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong>Top items:</strong>
                    <ul className="mt-1 space-y-1">
                      {daySummary.topSellingItems.slice(0, 5).map((item, i) => (
                        <li key={i}>{item.name}: {item.quantity} sold</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Select date and click Load.</p>
              )}
              <div className="mt-6 flex justify-end gap-2">
                {daySummary && (
                  <button onClick={() => window.print()} className="bg-muted px-4 py-2 rounded-lg font-medium">Print</button>
                )}
                <button onClick={() => setShowDayReport(false)} className="bg-primary text-white px-4 py-2 rounded-lg font-medium">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
